import { useCallback, useRef, useState } from 'react';
import { parsePhoneNumber } from 'react-phone-number-input';
import { adminApiClient } from '@/api-services/hooks/adminApiClient';

export type EntityType = 'driver' | 'rider' | 'admin';

interface DuplicateState {
  email: string | null;
  phone: string | null;
}

/**
 * Hook to check for duplicate email / phone on blur.
 * Returns per-field error messages and check callbacks.
 *
 * @param entityType - 'driver' | 'rider' | 'admin'
 * @param excludeId  - ID of the current entity being edited (skip self-match)
 */
export function useDuplicateCheck(entityType: EntityType, excludeId?: number | string) {
  const [duplicateErrors, setDuplicateErrors] = useState<DuplicateState>({ email: null, phone: null });
  const abortRef = useRef<Record<string, AbortController>>({});

  const clearError = useCallback((field: 'email' | 'phone') => {
    setDuplicateErrors((prev) => ({ ...prev, [field]: null }));
  }, []);

  /**
   * Check if the given email already exists for the entity type.
   * Returns true if duplicate found.
   */
  const checkEmail = useCallback(
    async (email: string): Promise<boolean> => {
      if (!email || !email.includes('@')) {
        clearError('email');
        return false;
      }

      // Abort any in-flight request for this field
      abortRef.current.email?.abort();
      const controller = new AbortController();
      abortRef.current.email = controller;

      try {
        let isDuplicate = false;

        if (entityType === 'admin') {
          const res = await adminApiClient.get<any>('/api/admin/verify-email', {
            email,
            type: 'ADMIN',
          }, { signal: controller.signal });
          isDuplicate = res === true || res?.data === true;
        } else if (entityType === 'driver') {
          const res = await adminApiClient.get<any>('/api/admin/verify-email', {
            email,
            type: 'DRIVER',
          }, { signal: controller.signal });
          isDuplicate = res === true || res?.data === true;
        } else if (entityType === 'rider') {
          const res = await adminApiClient.get<any>('/api/admin/verify-email', {
            email,
            type: 'RIDER',
          }, { signal: controller.signal });
          isDuplicate = res === true || res?.data === true;
        }

        if (isDuplicate) {
          setDuplicateErrors((prev) => ({ ...prev, email: 'This email is already in use' }));
        } else {
          clearError('email');
        }
        return isDuplicate;
      } catch (err: any) {
        if (err?.name === 'AbortError') return false;
        // On network error, clear so we don't block the form
        clearError('email');
        return false;
      }
    },
    [entityType, excludeId, clearError]
  );

  /**
   * Check if the given phone number already exists for the entity type.
   * Returns true if duplicate found.
   */
  const checkPhone = useCallback(
    async (phone: string): Promise<boolean> => {
      if (!phone || phone.length < 6) {
        clearError('phone');
        return false;
      }

      abortRef.current.phone?.abort();
      const controller = new AbortController();
      abortRef.current.phone = controller;

      // Extract national number (without country code) from E.164 format
      const parsed = parsePhoneNumber(phone);
      const nationalNumber = parsed?.nationalNumber || phone.replace(/^\+\d+/, '');

      try {
        let isDuplicate = false;

        const typeMap: Record<EntityType, string> = { admin: 'ADMIN', driver: 'DRIVER', rider: 'RIDER' };
        const res = await adminApiClient.get<any>('/api/admin/verify-mobile', {
          mobileNumberWithOutCountryCode: nationalNumber,
          type: typeMap[entityType],
        }, { signal: controller.signal });
        isDuplicate = res === true || res?.data === true;

        if (isDuplicate) {
          setDuplicateErrors((prev) => ({ ...prev, phone: 'This phone number is already in use' }));
        } else {
          clearError('phone');
        }
        return isDuplicate;
      } catch (err: any) {
        if (err?.name === 'AbortError') return false;
        clearError('phone');
        return false;
      }
    },
    [entityType, excludeId, clearError]
  );

  return { duplicateErrors, checkEmail, checkPhone, clearError };
}
